import { useState, useEffect, useRef } from 'react';

export default function GettingStarted() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/stackway-security-assessment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section ref={sectionRef} id="getting-started" className="py-24 bg-gradient-to-b from-white to-[#f7fafc] relative overflow-hidden">
      {/* Background illustration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[1400px] h-full opacity-5">
        <img 
          src="https://readdy.ai/api/search-image?query=Abstract%20collaboration%20and%20teamwork%20network%20pattern%2C%20minimalist%20geometric%20design%20with%20connecting%20lines%20and%20nodes%2C%20light%20background%20with%20cyan%20accents%20showing%20partnership%20and%20communication%20concept%2C%20modern%20tech%20illustration&width=1400&height=800&seq=getting-started-bg-pattern-001&orientation=landscape"
          alt="Background Pattern"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Centered header */}
        <div className={`text-center mb-16 max-w-3xl mx-auto transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block px-4 py-2 bg-[#06b6d4]/10 rounded-full mb-6">
            <span className="text-[#06b6d4] text-sm font-semibold uppercase tracking-wider">Bắt đầu ngay hôm nay</span>
          </div>
          <h2 className="font-['Inter',sans-serif] text-4xl md:text-5xl font-bold text-[#1a202c] mb-6 leading-tight">
            Bắt đầu từ đâu?
          </h2>
          <p className="text-lg text-[#4a5568] leading-relaxed">
            Mỗi hệ thống đều có đặc thú riêng. Chúng tôi sẵn sàng lắng nghe và đề xuất <strong className="text-[#1a202c]">giải pháp phù hợp nhất với doanh nghiệp của bạn</strong>.
          </p>
        </div>

        {/* Main content: Image + Form */}
        <div className="grid lg:grid-cols-5 gap-12 items-center max-w-[1280px] mx-auto">
          {/* Left: Visual (2 columns) */}
          <div className={`lg:col-span-2 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#e2e8f0] bg-white shadow-xl group hover:shadow-2xl transition-all duration-500">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20collaboration%20and%20consultation%20meeting%2C%20modern%20abstract%20illustration%20showing%20partnership%20and%20advisory%20services%20with%20people%20working%20together%2C%20clean%20minimalist%20style%20with%20cyan%20accents%20on%20light%20background%2C%20corporate%20consultation%20concept&width=600&height=700&seq=getting-started-consultation-visual-001&orientation=portrait"
                  alt="Consultation"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Contact info overlay */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 border border-[#e2e8f0] shadow-lg animate-[fadeInUp_0.8s_ease-out_0.6s_both]">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-[#06b6d4]/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-mail-line text-lg text-[#06b6d4]"></i>
                    </div>
                    <div>
                      <div className="text-xs text-[#4a5568]">Email</div>
                      <div className="font-semibold text-[#1a202c] text-sm group-hover:text-[#06b6d4] transition-colors">contact@stackway.vn</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-[#06b6d4]/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-phone-line text-lg text-[#06b6d4]"></i>
                    </div>
                    <div>
                      <div className="text-xs text-[#4a5568]">Hotline</div>
                      <div className="font-semibold text-[#1a202c] text-sm group-hover:text-[#06b6d4] transition-colors">1900 xxxx</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form (3 columns) */}
          <div className={`lg:col-span-3 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <form 
              id="stackway-security-assessment"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10 border-2 border-[#e2e8f0] shadow-xl hover:shadow-2xl transition-shadow duration-500"
            >
              <h3 className="font-['Inter',sans-serif] text-2xl md:text-3xl font-bold text-[#1a202c] mb-2">
                Đăng ký tư vấn miễn phí
              </h3>
              <p className="text-[#4a5568] mb-8">
                Điền thông tin bên dưới, chúng tôi sẽ liên hệ trong vòng 24h
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="group">
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1a202c] mb-2">
                    Họ và tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#2a4b7c] focus:ring-2 focus:ring-[#2a4b7c]/20 outline-none transition-all text-sm group-hover:border-[#2a4b7c]/50"
                    placeholder="Nguyễn Văn A"
                  />
                </div>
                <div className="group">
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1a202c] mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#2a4b7c] focus:ring-2 focus:ring-[#2a4b7c]/20 outline-none transition-all text-sm group-hover:border-[#2a4b7c]/50"
                    placeholder="email@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="group">
                  <label htmlFor="company" className="block text-sm font-semibold text-[#1a202c] mb-2">
                    Công ty
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#2a4b7c] focus:ring-2 focus:ring-[#2a4b7c]/20 outline-none transition-all text-sm group-hover:border-[#2a4b7c]/50"
                    placeholder="Tên công ty"
                  />
                </div>
                <div className="group">
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#1a202c] mb-2">
                    Số điện thoại
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#2a4b7c] focus:ring-2 focus:ring-[#2a4b7c]/20 outline-none transition-all text-sm group-hover:border-[#2a4b7c]/50"
                    placeholder="0912 345 678"
                  />
                </div>
              </div>

              <div className="mb-6 group">
                <label htmlFor="message" className="block text-sm font-semibold text-[#1a202c] mb-2">
                  Nội dung cần tư vấn
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] focus:border-[#06b6d4] focus:ring-2 focus:ring-[#06b6d4]/20 outline-none transition-all resize-none text-sm group-hover:border-[#06b6d4]/50"
                  placeholder="Mô tả ngắn gọn về hệ thống và nhu cầu của bạn..."
                ></textarea>
                <div className="text-right text-xs text-[#4a5568] mt-1">
                  {formData.message.length}/500 ký tự
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2a4b7c] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#1e3a63] hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                    Đang gửi...
                  </>
                ) : (
                  <>
                    Gửi yêu cầu tư vấn
                    <i className="ri-send-plane-fill text-xl"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-[fadeInUp_0.5s_ease-out]">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  <p className="text-green-800 text-sm">
                    Cảm ơn bạn! Chúng tôi sẽ liên hệ trong thời gian sớm nhất.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 animate-[fadeInUp_0.5s_ease-out]">
                  <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                  <p className="text-red-800 text-sm">
                    Có lỗi xảy ra. Vui lòng thử lại sau.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}